import { useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';

/**
 * Blocks in-app navigation when `when` is true and asks for confirmation.
 *
 * Important: do not put the whole `blocker` object in effect deps — its identity
 * changes frequently from the router and would cancel the scheduled confirm dialog,
 * leaving navigation stuck in the "blocked" state (routes become "unreachable").
 *
 * Same rationale for deferring proceed/reset: avoids React 18
 * "Should not already be working" during passive-effect flush.
 */
export function usePrompt({ when, message }) {
  const blocker = useBlocker(when);
  const messageRef = useRef(message);
  messageRef.current = message;

  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  useEffect(() => {
    if (blocker.state !== 'blocked') return undefined;

    const timeoutId = window.setTimeout(() => {
      const ok = window.confirm(messageRef.current);
      const b = blockerRef.current;
      if (ok) {
        window.setTimeout(() => b.proceed(), 0);
      } else {
        window.setTimeout(() => b.reset(), 0);
      }
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [blocker.state]);

  useEffect(() => {
    if (blocker.state !== 'blocked' || when) return undefined;

    const timeoutId = window.setTimeout(() => {
      blockerRef.current.reset();
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [blocker.state, when]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (when) {
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [when, message]);
}
